import React, { useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  DeleteOutlineOutlined, DragIndicatorOutlined, AddOutlined, LockOutlined,
  StarOutlined, InfoOutlined, ConfirmationNumberOutlined, GridViewOutlined, ViewColumnOutlined,
  CalendarMonthOutlined, AccessTimeOutlined, VideocamOutlined, CheckCircleOutline,
} from '@mui/icons-material';
import { FIELD_COLOR, SELECTION_COLOR, FIELD_TYPES } from './constants';
import StepStrip from './StepStrip';
import { uid, makeField } from './helpers';


// ─────────────────────────────────────────────────────────────────────────────
// Hero selection IDs — shared constants for canvas ↔ inspector routing
// ─────────────────────────────────────────────────────────────────────────────
export const HERO_BANNER   = '__hero_banner__';
export const HERO_TAGLINE  = '__hero_tagline__';
export const HERO_TITLE    = '__hero_title__';
export const HERO_SUBTITLE = '__hero_subtitle__';

// ─────────────────────────────────────────────────────────────────────────────
// MediaPanel — renders the banner using branding.bannerBg (CSS background)
// Falls back to old mediaUrl/primaryColor for backwards compatibility.
// ─────────────────────────────────────────────────────────────────────────────

function MediaPanel({ branding, children, sx: sxProp, selectedId, onSelect }) {
  const br           = branding || {};
  const bannerBg     = br.bannerBg || {};
  const primaryColor = br.primaryColor || '#1565C0';

  const isVideo    = bannerBg.type === 'video';
  const videoUrl   = bannerBg.videoUrl || '';
  const bgRaw      = bannerBg.background || '';   // CSS set by BackgroundSection
  const isImage    = !isVideo && bgRaw.startsWith('url(');
  const isGradient = !isVideo && bgRaw.includes('gradient');
  const isSolid    = !isVideo && !isImage && !isGradient;

  // When no bannerBg set at all, fall back to a gradient from primaryColor
  const fallbackBg = `linear-gradient(135deg, ${primaryColor} 0%, ${alpha(primaryColor, 0.6)} 100%)`;
  const bgCss      = isVideo ? 'transparent' : (bgRaw || fallbackBg);

  // backgroundSize / backgroundPosition — only apply when bgRaw is a plain url() (not a shorthand)
  // BackgroundSection writes full shorthands like `url(...) center/cover no-repeat`
  // so we let the browser handle those via the `background` shorthand and skip separate props
  const isShorthand = bgRaw.includes('center') || bgRaw.includes('cover') || bgRaw.includes('no-repeat');
  const bgSize      = isShorthand ? undefined : (bannerBg.backgroundSize || 'cover');
  const bgPos       = isShorthand ? undefined : (bannerBg.backgroundPosition || 'center');

  const overlayColor   = br.mediaOverlayColor  || '#000000';
  const overlayOpacity = br.mediaOverlayOpacity ?? 0.45;

  const hexToRgba = (hex, op) => {
    const r = parseInt(hex.slice(1, 3), 16) || 0;
    const g = parseInt(hex.slice(3, 5), 16) || 0;
    const b = parseInt(hex.slice(5, 7), 16) || 0;
    return `rgba(${r},${g},${b},${op})`;
  };
  const overlayRgba = overlayColor.startsWith('#')
    ? hexToRgba(overlayColor, overlayOpacity)
    : overlayColor.startsWith('rgba')
      ? overlayColor.replace(/,\s*[\d.]+\)$/, `,${overlayOpacity})`)
      : `rgba(0,0,0,${overlayOpacity})`;

  const showOverlay = overlayOpacity > 0 && (isImage || isVideo);

  const isSel = selectedId === HERO_BANNER;

  return (
    <Box
      onClick={onSelect ? (e) => { e.stopPropagation(); onSelect(HERO_BANNER); } : undefined}
      sx={{
        position: 'relative', overflow: 'hidden', ...sxProp,
        cursor: onSelect ? 'pointer' : 'default',
        outline: isSel ? `2px solid ${FIELD_COLOR}` : '2px solid transparent',
        outlineOffset: isSel ? -2 : 0,
        transition: 'outline-color 0.15s',
        '&:hover': onSelect ? { outline: `2px solid ${isSel ? FIELD_COLOR : alpha(FIELD_COLOR, 0.4)}` } : {},
      }}
    >
      {/* Background layer (image / solid / gradient) */}
      {!isVideo && (
        <Box sx={{
          position: 'absolute', inset: 0,
          background: bgCss,
          ...(bgSize ? { backgroundSize: bgSize } : {}),
          ...(bgPos  ? { backgroundPosition: bgPos }  : {}),
        }} />
      )}

      {/* Video */}
      {isVideo && videoUrl && (
        <video autoPlay muted loop playsInline
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}>
          <source src={videoUrl} />
        </video>
      )}

      {/* Overlay */}
      {showOverlay && (
        <Box sx={{ position: 'absolute', inset: 0, background: overlayRgba }} />
      )}

      {/* Selection chip */}
      {isSel && (
        <Box sx={{ position: 'absolute', top: 6, left: 6, zIndex: 10, px: 0.9, py: 0.2, background: FIELD_COLOR, borderRadius: 1, fontSize: '0.58rem', fontWeight: 700, color: '#fff', letterSpacing: '0.5px', textTransform: 'uppercase', pointerEvents: 'none' }}>
          Banner
        </Box>
      )}

      <Box sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: bannerBg.contentAlign || 'center', p: 3 }}>
        {children}
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// InlineEditText — contentEditable span that saves on blur / Enter
// ─────────────────────────────────────────────────────────────────────────────
function InlineEditText({ value, onChange, placeholder, sx: sxProp, selectedId, heroId, onSelect, multiline }) {
  const ref       = React.useRef(null);
  const isSel     = selectedId === heroId;
  const [editing, setEditing] = React.useState(false);

  // Sync content when value changes externally (but not while editing)
  React.useEffect(() => {
    if (!editing && ref.current && ref.current.innerText !== (value || '')) {
      ref.current.innerText = value || '';
    }
  }, [value, editing]);

  const handleClick = (e) => {
    e.stopPropagation();
    if (!isSel && onSelect) { onSelect(heroId); return; }
    // Already selected — enter edit mode
    setEditing(true);
    setTimeout(() => {
      if (ref.current) {
        ref.current.focus();
        // Move cursor to end
        const range = document.createRange();
        range.selectNodeContents(ref.current);
        range.collapse(false);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }, 10);
  };

  const commit = () => {
    setEditing(false);
    const text = ref.current?.innerText || '';
    if (text !== value) onChange(text);
  };

  const display = value || placeholder || '';

  return (
    <Box sx={{ position: 'relative', display: 'inline-block', width: '100%' }}>
      {/* Selection chip label */}
      {isSel && (
        <Box sx={{ position: 'absolute', top: -18, left: 0, zIndex: 20, px: 0.75, py: 0.15, background: FIELD_COLOR, borderRadius: 0.75, fontSize: '0.55rem', fontWeight: 700, color: '#fff', letterSpacing: '0.4px', textTransform: 'uppercase', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
          {heroId === HERO_TAGLINE ? 'Tagline' : heroId === HERO_TITLE ? 'Title' : 'Subtitle'}
        </Box>
      )}
      <Box
        ref={ref}
        contentEditable={editing}
        suppressContentEditableWarning
        onClick={handleClick}
        onBlur={commit}
        onKeyDown={e => {
          if (!multiline && e.key === 'Enter') { e.preventDefault(); ref.current?.blur(); }
          if (e.key === 'Escape') { ref.current.innerText = value || ''; ref.current?.blur(); }
          e.stopPropagation();
        }}
        sx={{
          display: 'block',
          outline: editing ? `2px solid ${FIELD_COLOR}` : isSel ? `1.5px solid ${alpha('#fff', 0.6)}` : '1.5px solid transparent',
          outlineOffset: 2,
          borderRadius: 0.75,
          cursor: editing ? 'text' : isSel ? 'text' : 'pointer',
          transition: 'outline-color 0.15s',
          '&:hover': { outline: `1.5px solid ${alpha('#fff', 0.5)}`, borderRadius: 0.75 },
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          minWidth: 40,
          color: !value ? alpha(sxProp?.color || '#fff', 0.4) : undefined,
          ...sxProp,
        }}
        dangerouslySetInnerHTML={editing ? undefined : { __html: display }}
      />
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HeroText overlay — each element is inline-editable on the canvas
// ─────────────────────────────────────────────────────────────────────────────
function HeroText({ settings, align, selectedId, onSelect, onSettingsChange }) {
  const textColor = settings?.heroTextColor || '#fff';
  const up = (k, v) => onSettingsChange && onSettingsChange({ ...settings, [k]: v });
  const hasContent = settings?.headerTitle || settings?.headerSubtitle || settings?.description;

  if (!onSettingsChange && !hasContent) return null;

  return (
    <Box sx={{ textAlign: align || 'left', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <InlineEditText
        value={settings?.headerTitle || ''}
        onChange={v => up('headerTitle', v)}
        placeholder="TAGLINE"
        heroId={HERO_TAGLINE}
        selectedId={selectedId}
        onSelect={onSelect}
        sx={{ fontSize: '0.65rem', fontWeight: 800, letterSpacing: '0.2em', textTransform: 'uppercase', color: alpha(textColor, 0.85) }}
      />
      <InlineEditText
        value={settings?.headerSubtitle || ''}
        onChange={v => up('headerSubtitle', v)}
        placeholder="Event Title"
        heroId={HERO_TITLE}
        selectedId={selectedId}
        onSelect={onSelect}
        sx={{ fontSize: '2rem', fontWeight: 800, color: textColor, lineHeight: 1.1, textShadow: '0 1px 10px rgba(0,0,0,0.3)' }}
      />
      <InlineEditText
        value={settings?.description || ''}
        onChange={v => up('description', v)}
        placeholder="Short description…"
        heroId={HERO_SUBTITLE}
        selectedId={selectedId}
        onSelect={onSelect}
        multiline
        sx={{ fontSize: '1rem', color: alpha(textColor, 0.85), lineHeight: 1.5 }}
      />
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FieldPreview — read-only render of a single field
// ─────────────────────────────────────────────────────────────────────────────

// ─── Inline editable label ────────────────────────────────────────────────────
function InlineLabel({ value, fieldId, onChange, required }) {
  const ref          = React.useRef(null);
  const lastId       = React.useRef(fieldId);
  const committedVal = React.useRef(value); // last value we committed or received

  // Initial mount
  React.useEffect(() => {
    if (ref.current) ref.current.innerText = value || '';
    committedVal.current = value;
  // eslint-disable-next-line 
  }, []);

  // Switching to a different field — always reset
  React.useEffect(() => {
    if (fieldId !== lastId.current) {
      lastId.current = fieldId;
      committedVal.current = value;
      if (ref.current) ref.current.innerText = value || '';
    }
  }, [fieldId, value]);

  // External value change (e.g. undo) — only sync if not from our own commit
  React.useEffect(() => {
    if (value !== committedVal.current && ref.current) {
      committedVal.current = value;
      ref.current.innerText = value || '';
    }
  }, [value]);

  const commit = () => {
    const text = ref.current?.innerText?.trim() || '';
    // Block empty — revert to committed value
    if (!text) {
      if (ref.current) ref.current.innerText = committedVal.current || '';
      return;
    }
    committedVal.current = text;
    if (text !== value) onChange(text);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
      <Box
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); ref.current?.blur(); } e.stopPropagation(); }}
        onClick={e => e.stopPropagation()}
        sx={{
          fontSize: '1rem', fontWeight: 600, color: '#1a1a2e',
          outline: 'none', cursor: 'text', borderRadius: 0.5,
          px: 0.25, minWidth: 40, display: 'inline-block',
          border: '1.5px solid transparent',
          transition: 'border-color 0.12s, background 0.12s',
          '&:hover': { borderColor: alpha(FIELD_COLOR, 0.35), background: alpha(FIELD_COLOR, 0.03) },
          '&:focus': { borderColor: FIELD_COLOR, background: alpha(FIELD_COLOR, 0.04) },
          '&:empty:before': { content: '"Label"', color: '#c0c8d4' },
        }}
      />
      {required && <Typography sx={{ color: '#ef4444', fontSize: '0.9rem', lineHeight: 1 }}>*</Typography>}
    </Box>
  );
}

function FieldPreview({ field, primaryColor, selectedId, onSelect, onFieldChange, onDelete }) {
  const col = primaryColor || FIELD_COLOR;
  const inp = { width: '100%', border: '1px solid #d4dbe6', borderRadius: 6, padding: '7px 10px', fontSize: '16px', fontFamily: 'inherit', background: '#fff', color: '#3c4555', outline: 'none', boxSizing: 'border-box' };

  if (field.type === 'heading') {
    const sizes = { h1: '1.5rem', h2: '1.25rem', h3: '1.05rem', h4: '0.95rem' };
    return <Box component={field.level || 'h3'} sx={{ m: 0, fontSize: sizes[field.level] || '1.05rem', fontWeight: 700, color: '#1a1a2e' }}>{field.content}</Box>;
  }
  if (field.type === 'divider') return <Box sx={{ borderTop: '1px solid #e8edf2', my: 0.5 }} />;
  if (field.type === 'selection_group') return <SelectionGroupFieldPreview field={field} primaryColor={col} />;
  if (field.type === 'column_group') return (
    <ColumnGroupPreview
      field={field}
      primaryColor={col}
      selectedId={selectedId}
      onSelect={onSelect || (() => {})}
      onFieldChange={onFieldChange || (() => {})}
      onDelete={onDelete || (() => {})}
    />
  );

  return (
    <Box>
      {field.label !== undefined && field.type !== 'divider' && field.type !== 'heading' && (
        <InlineLabel
          value={field.label}
          fieldId={field.id}
          required={field.required}
          onChange={label => onFieldChange && onFieldChange({ ...field, label })}
        />
      )}
      {['text', 'email', 'phone', 'number', 'tel', 'password', 'firstName', 'lastName'].includes(field.type) && <input readOnly style={inp} placeholder={field.placeholder || ''} />}
      {field.type === 'textarea'  && <textarea readOnly rows={field.rows || 4} style={{ ...inp, resize: 'none' }} placeholder={field.placeholder || ''} />}
      {field.type === 'select'    && <select style={inp} disabled><option>Select an option…</option>{(field.options || []).map((o, i) => <option key={i}>{o.label}</option>)}</select>}
      {(field.type === 'radio' || field.type === 'checkbox') && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {(field.options || []).map((o, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <input readOnly type={field.type} style={{ width: 14, height: 14, accentColor: col }} />
              <Typography sx={{ fontSize: '1rem', color: '#3c4555' }}>{o.label}</Typography>
            </Box>
          ))}
        </Box>
      )}
      {field.type === 'toggle' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 36, height: 20, borderRadius: 10, background: '#d4dbe6', display: 'flex', alignItems: 'center', px: '3px' }}>
            <Box sx={{ width: 14, height: 14, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
          </Box>
        </Box>
      )}
      {field.type === 'date'   && <input readOnly type="date" style={inp} />}
      {field.type === 'file'   && <Box sx={{ border: '2px dashed #d4dbe6', borderRadius: '8px', p: 2, textAlign: 'center' }}><Typography sx={{ fontSize: '1rem', color: '#9ea8b3' }}>Drop file or <span style={{ color: col }}>browse</span></Typography></Box>}
      {field.type === 'rating' && <Box sx={{ display: 'flex', gap: 0.5 }}>{Array.from({ length: field.max || 5 }).map((_, i) => <StarOutlined key={i} sx={{ fontSize: 22, color: '#fbbf24' }} />)}</Box>}
      {field.type === 'scale'  && <Box sx={{ display: 'flex', gap: '3px' }}>{Array.from({ length: (field.max || 10) - (field.min || 1) + 1 }).map((_, i) => <Box key={i} sx={{ flex: 1, py: '5px', border: '1px solid #d4dbe6', borderRadius: '4px', fontSize: '0.7rem', textAlign: 'center', color: '#5a6474' }}>{(field.min || 1) + i}</Box>)}</Box>}
      {field.helperText && <Typography sx={{ fontSize: '0.875rem', color: '#9ea8b3', mt: 0.75 }}>{field.helperText}</Typography>}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SelectionGroupFieldPreview
// Inline canvas preview for a selection_group field living inside a fields step
// ─────────────────────────────────────────────────────────────────────────────

function SelectionGroupFieldPreview({ field, primaryColor }) {
  const col   = primaryColor || FIELD_COLOR;
  const mode  = field.selectionMode || 'single';
  const cols  = field.columns || 3;
  const items = field.items  || [];
  const [selected, setSelected] = useState([]);

  const toggle = (id) => {
    if (mode === 'single') {
      setSelected(prev => prev.includes(id) ? [] : [id]);
    } else {
      setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    }
  };

  return (
    <Box>
      {/* Group header */}
      {(field.badge || field.label || field.subtitle) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
          {field.badge && (
            <Box sx={{ px: 0.75, py: 0.2, borderRadius: '6px', background: '#eef2f7', flexShrink: 0 }}>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#5a7184' }}>{field.badge}</Typography>
            </Box>
          )}
          <Box>
            {field.label && <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#1a2c42' }}>{field.label}</Typography>}
            {field.subtitle && <Typography sx={{ fontSize: '0.875rem', color: '#9ea8b3' }}>{field.subtitle}</Typography>}
          </Box>
        </Box>
      )}

      {/* Card grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(cols, 3)}, 1fr)`, gap: 1 }}>
        {items.map(item => {
          const isSel     = selected.includes(item.id);
          const hasPrice  = item.price !== null && item.price !== undefined;
          return (
            <Box key={item.id} onClick={() => toggle(item.id)}
              sx={{ borderRadius: '10px', border: `1.5px solid ${isSel ? col : '#e8edf2'}`, background: isSel ? alpha(col, 0.04) : '#fff', p: 1.25, cursor: 'pointer', transition: 'all 0.12s', boxShadow: isSel ? `0 0 0 2px ${alpha(col, 0.15)}` : 'none', '&:hover': { borderColor: alpha(col, 0.4) } }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5 }}>
                <Box sx={{ width: 26, height: 26, borderRadius: '7px', background: isSel ? alpha(col, 0.1) : '#f0f4f8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ConfirmationNumberOutlined sx={{ fontSize: 14, color: isSel ? col : '#9ea8b3' }} />
                </Box>
                <Box sx={{ width: 14, height: 14, borderRadius: mode === 'single' ? '50%' : '3px', border: `2px solid ${isSel ? col : '#d4dbe6'}`, background: isSel ? col : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {isSel && <Box sx={{ width: mode === 'single' ? 5 : 7, height: mode === 'single' ? 5 : 7, borderRadius: mode === 'single' ? '50%' : '1px', background: '#fff' }} />}
                </Box>
              </Box>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: isSel ? col : '#1a1a2e', lineHeight: 1.3 }}>{item.title}</Typography>
              {item.description && <Typography sx={{ fontSize: '0.75rem', color: '#9ea8b3', mt: 0.25 }}>{item.description}</Typography>}
              {(hasPrice || item.priceLabel) && (
                <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid #f0f2f5' }}>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: col }}>
                    {item.priceLabel || (item.price === 0 ? 'Free' : `${item.currency || 'PHP'} ${item.price}`)}
                  </Typography>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {field.required && (
        <Typography sx={{ mt: 0.5, fontSize: '0.65rem', color: '#ef4444' }}>* Required</Typography>
      )}
      {field.helperText && (
        <Typography sx={{ mt: 0.5, fontSize: '0.875rem', color: '#9ea8b3' }}>{field.helperText}</Typography>
      )}
    </Box>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// ColumnGroupPreview
// Each column holds at most 1 field.
// Drag FROM palette or outer canvas → insert into empty column (or replace if full).
// Drag OUT of column → sets window.__draggedColField__ so outer canvas can pick it up.
// ─────────────────────────────────────────────────────────────────────────────

function ColumnGroupPreview({ field, primaryColor, selectedId, onSelect, onFieldChange, onDelete }) {
  const col  = primaryColor || FIELD_COLOR;
  const cols = field.columns || [];

  const [dropAtColId, setDropAtColId] = React.useState(null); // which col is being hovered

  const updateField = (updated) => onFieldChange({ ...field, ...updated });

  // ── Drop into a column ────────────────────────────────────────────────────
  const handleColDrop = (e, colId) => {
    e.preventDefault();
    e.stopPropagation();

    const paletteType  = e.dataTransfer.getData('fieldType');
    const outerFieldId = e.dataTransfer.getData('outerFieldId');
    const innerFieldId = e.dataTransfer.getData('innerColFieldId');

    const targetCol = cols.find(c => c.id === colId);
    if (!targetCol) return;

    // Already has a field — only allow swapping from another column in this same group
    const existingField = (targetCol.fields || [])[0] || null;

    if (innerFieldId) {
      // Moving a field from one column of THIS group to another column
      const srcCol = cols.find(c => (c.fields || []).some(f => f.id === innerFieldId));
      if (!srcCol || srcCol.id === colId) { setDropAtColId(null); return; }
      const movedField = srcCol.fields.find(f => f.id === innerFieldId);

      const newCols = cols.map(c => {
        if (c.id === srcCol.id) return { ...c, fields: existingField ? [existingField] : [] };
        if (c.id === colId)     return { ...c, fields: [movedField] };
        return c;
      });
      // If target had a field, swap it into the source column
      if (existingField) {
        const withSwap = newCols.map(c =>
          c.id === srcCol.id ? { ...c, fields: [existingField] } : c
        );
        updateField({ columns: withSwap });
      } else {
        updateField({ columns: newCols });
      }

    } else if (paletteType) {
      // New field from palette — only allow if column is empty
      if (existingField) { setDropAtColId(null); return; }
      const nf = makeField(paletteType);
      if (!nf) return;
      updateField({ columns: cols.map(c => c.id === colId ? { ...c, fields: [nf] } : c) });
      onSelect(nf.id);

    } else if (outerFieldId) {
      // Field dragged from outer canvas — signal parent to move it
      // Only allow if column is empty
      if (existingField) { setDropAtColId(null); return; }
      onFieldChange({ ...field, __adoptOuterField__: { fieldId: outerFieldId, colId } });
    }

    setDropAtColId(null);
  };

  // ── DropLine ─────────────────────────────────────────────────────────────
  const DropLine = ({ show }) => show ? (
    <Box sx={{ height: 2, my: '3px', background: col, borderRadius: 1, position: 'relative', zIndex: 10, boxShadow: `0 0 6px ${alpha(col, 0.5)}`, flexShrink: 0 }}>
      <Box sx={{ position: 'absolute', left: -4, top: -3, width: 8, height: 8, borderRadius: '50%', background: col }} />
    </Box>
  ) : null;

  return (
    <Box sx={{ display: 'flex', gap: '6px', width: '100%' }}>
      {cols.map((col_) => {
        const colField    = (col_.fields || [])[0] || null; // max 1 field per column
        const isTarget    = dropAtColId === col_.id;
        const isSelected  = colField && selectedId === colField.id;
        const isEmpty     = !colField;

        return (
          <Box
            key={col_.id}
            onDragOver={e => { e.preventDefault(); e.stopPropagation(); setDropAtColId(col_.id); }}
            onDrop={e => handleColDrop(e, col_.id)}
            onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDropAtColId(null); }}
            sx={{
              flex: 1, minWidth: 0,
              borderRadius: '8px',
              border: `1.5px dashed ${isTarget ? col : alpha(col, 0.18)}`,
              background: isTarget ? alpha(col, 0.04) : 'transparent',
              p: '8px',
              transition: 'border-color 0.12s, background 0.12s',
              minHeight: 52,
              display: 'flex', flexDirection: 'column',
            }}
          >
            {/* Show drop line at top when dragging over a column that has a field */}
            {colField && <DropLine show={isTarget} />}

            {/* The single field in this column */}
            {colField && (
              <Box
                draggable
                onDragStart={e => {
                  e.stopPropagation();
                  // Store on dataTransfer so both inner-column and outer-canvas can read it
                  e.dataTransfer.setData('innerColFieldId', colField.id);
                  // Also store full field object on window for outer canvas pickup
                  window.__draggedColField__ = {
                    field:      colField,
                    colId:      col_.id,
                    columnGroupId: field.id,
                  };
                }}
                onDragEnd={() => { window.__draggedColField__ = null; }}
                onClick={e => { e.stopPropagation(); onSelect(colField.id); }}
                sx={{
                  borderRadius: '6px',
                  border: `1.5px solid ${isSelected ? col : 'transparent'}`,
                  background: isSelected ? alpha(col, 0.02) : 'transparent',
                  p: '6px 8px',
                  cursor: 'pointer', transition: 'all 0.1s',
                  '&:hover': { border: `1.5px solid ${alpha(col, 0.25)}` },
                }}
              >
                {/* Field header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: colField.type === 'divider' ? 0 : 0.5 }}>
                  <Box sx={{ cursor: 'grab', color: '#d4dbe6', display: 'flex', '&:active': { cursor: 'grabbing' }, '&:hover': { color: '#9ea8b3' } }}>
                    <DragIndicatorOutlined sx={{ fontSize: 12 }} />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, px: 0.5, py: 0.15, borderRadius: 0.75, background: alpha(col, 0.07) }}>
                    <Box sx={{ color: col, display: 'flex', transform: 'scale(0.75)' }}>{FIELD_TYPES[colField._type || colField.type]?.icon}</Box>
                    <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: col, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {FIELD_TYPES[colField._type || colField.type]?.label || colField.type}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }} />
                  {isSelected && (
                    <Box
                      onClick={e => { e.stopPropagation(); updateField({ columns: cols.map(c => c.id === col_.id ? { ...c, fields: [] } : c) }); }}
                      sx={{ cursor: 'pointer', color: '#dc2626', display: 'flex', opacity: 0.7, '&:hover': { opacity: 1 } }}
                    >
                      <DeleteOutlineOutlined sx={{ fontSize: 11 }} />
                    </Box>
                  )}
                </Box>
                <FieldPreview
                  field={colField}
                  primaryColor={col}
                  selectedId={selectedId}
                  onSelect={onSelect}
                  onFieldChange={cf => updateField({ columns: cols.map(c => c.id === col_.id ? { ...c, fields: [cf] } : c) })}
                  onDelete={() => updateField({ columns: cols.map(c => c.id === col_.id ? { ...c, fields: [] } : c) })}
                />
              </Box>
            )}

            {/* Empty column placeholder */}
            {isEmpty && (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 1 }}>
                <Typography sx={{ fontSize: '0.65rem', color: '#c0c8d4', pointerEvents: 'none' }}>
                  {isTarget ? '↓ Drop here' : 'Empty'}
                </Typography>
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FormCardPreview — interactive fields canvas (drag, select, delete)
// Now receives activeStepId to drive the in-card step indicator.
// ─────────────────────────────────────────────────────────────────────────────

function FormCardPreview({ fields, selectedId, onSelect, onDelete, onFieldChange, onDragStart, onDragOver, onDrop, onDropNew, dragOverId, steps, activeStepId, stepHandlers, branding, variant, onNextStep, onShowSuccess }) {
  const col     = branding?.primaryColor || '#1565C0';
  const cardBg  = branding?.cardColor    || '#fff';
  const isFlush = variant === 'flush' || variant === 'incard';

  const inner = (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: isFlush ? 1 : undefined, minHeight: isFlush ? 0 : undefined }}>

      {/* ── StepStrip — editable step indicator inside the card ──────────── */}
      {steps?.length > 0 && stepHandlers && (
        <Box sx={{ borderBottom: '1px solid #f0f2f5', flexShrink: 0 }}>
          <StepStrip
            steps={steps}
            activeStepId={activeStepId}
            onSwitch={stepHandlers.onSwitch}
            onAdd={stepHandlers.onAdd}
            onRename={stepHandlers.onRename}
            onDelete={stepHandlers.onDelete}
            onReorder={stepHandlers.onReorder}
          />
        </Box>
      )}

      {/* ── Fields list ───────────────────────────────────────────────────── */}
      <Box sx={{ px: isFlush ? 3 : 4, pt: 3, pb: 1, flex: isFlush ? 1 : undefined, overflowY: isFlush ? 'auto' : undefined }}>
        {fields.map(field => (
          <Box key={field.id}
            onClick={e => { e.stopPropagation(); onSelect(field.id); }}
            onDragOver={e => { e.preventDefault(); onDragOver(field.id); }}
            onDrop={e => onDrop(e, field.id)}
            sx={{ position: 'relative', borderRadius: '8px', border: `1.5px solid ${selectedId === field.id ? col : dragOverId === field.id ? alpha(col, 0.4) : 'transparent'}`, background: selectedId === field.id ? alpha(col, 0.02) : 'transparent', p: 1, mb: 0.75, cursor: 'pointer', transition: 'all 0.1s', '&:hover': { border: `1.5px solid ${alpha(col, 0.3)}` } }}>
            {dragOverId === field.id && <Box sx={{ position: 'absolute', top: -2, left: '10%', width: '80%', height: 2, borderRadius: 1, background: col }} />}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: field.type === 'divider' ? 0 : 0.75 }}>
              <Box draggable onDragStart={e => { e.stopPropagation(); e.dataTransfer.setData('outerFieldId', field.id); onDragStart(field.id); }} onClick={e => e.stopPropagation()} sx={{ cursor: 'grab', color: '#d4dbe6', display: 'flex', '&:hover': { color: '#9ea8b3' } }}>
                <DragIndicatorOutlined sx={{ fontSize: 14 }} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 0.6, py: 0.2, borderRadius: 1, background: alpha(col, 0.07) }}>
                <Box sx={{ color: col, display: 'flex', transform: 'scale(0.8)' }}>{FIELD_TYPES[field._type || field.type]?.icon}</Box>
                <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: col, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{FIELD_TYPES[field._type || field.type]?.label || field.type}</Typography>
              </Box>
              <Box sx={{ flex: 1 }} />
              {selectedId === field.id && (
                field._systemField ? (
                  <Box title="Required auth field — cannot be deleted" sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 0.75, py: 0.2, borderRadius: 1, background: alpha('#f59e0b', 0.1), border: '1px solid #fde68a' }}>
                    <LockOutlined sx={{ fontSize: 10, color: '#d97706' }} />
                    <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: '#d97706', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Locked</Typography>
                  </Box>
                ) : (
                  <IconButton size="small" onClick={e => { e.stopPropagation(); onDelete(field.id); }} sx={{ color: '#dc2626', width: 18, height: 18 }}>
                    <DeleteOutlineOutlined sx={{ fontSize: 12 }} />
                  </IconButton>
                )
              )}
            </Box>
            <FieldPreview
              field={field}
              primaryColor={col}
              selectedId={selectedId}
              onSelect={onSelect}
              onFieldChange={(updatedChild) => {
                if (updatedChild.__adoptOuterField__) {
                  const { fieldId: outerFid, colId } = updatedChild.__adoptOuterField__;
                  const outerField = fields.find(f => f.id === outerFid);
                  if (!outerField || outerFid === field.id) return;
                  const updatedCols = (field.columns || []).map(c =>
                    c.id === colId ? { ...c, fields: [outerField] } : c
                  );
                  if (onFieldChange) onFieldChange({
                    __batchOps__: [
                      { op: 'delete', id: outerFid },
                      { op: 'update', field: { ...field, columns: updatedCols } },
                    ]
                  });
                  return;
                }
                // Direct field update (e.g. label change) — pass through as-is
                if (onFieldChange) onFieldChange(updatedChild);
              }}
              onDelete={onDelete}
            />
          </Box>
        ))}

        {fields.length === 0 && (
          <Box onDragOver={e => { e.preventDefault(); onDragOver('__canvas__'); }}
            onDrop={e => { e.preventDefault(); const ft = e.dataTransfer.getData('fieldType'); if (ft) onDropNew(ft, null); }}
            sx={{ border: `2px dashed ${dragOverId === '__canvas__' ? col : alpha(col, 0.2)}`, borderRadius: '10px', p: 4, textAlign: 'center', background: dragOverId === '__canvas__' ? alpha(col, 0.04) : alpha(col, 0.01), transition: 'all 0.12s' }}>
            <Typography sx={{ fontSize: '1rem', color: '#9ea8b3' }}>Drag fields here to build your form</Typography>
          </Box>
        )}

        {fields.length > 0 && (
          <Box
            onDragOver={e => { e.preventDefault(); e.stopPropagation(); onDragOver('__bottom__'); }}
            onDrop={e => { e.preventDefault(); e.stopPropagation(); const ft = e.dataTransfer.getData('fieldType'); if (ft) onDropNew(ft, null); }}
            sx={{ height: 32, borderRadius: '6px', border: `2px dashed ${dragOverId === '__bottom__' ? col : 'transparent'}`, background: dragOverId === '__bottom__' ? alpha(col, 0.04) : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.1s' }}>
            {dragOverId === '__bottom__' && <Typography sx={{ fontSize: '0.72rem', color: col, fontWeight: 600 }}>Drop here</Typography>}
          </Box>
        )}
      </Box>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: isFlush ? 3 : 4, py: 2.5, borderTop: '1px solid #e8edf2', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <InfoOutlined sx={{ fontSize: 13, color: '#c0c8d4' }} />
          <Typography sx={{ fontSize: '0.68rem', color: '#c0c8d4' }}>Your data is secured</Typography>
        </Box>
        {/* Last step → Submit, otherwise → Next */}
        {(() => {
          const isLastStep = !steps?.length || steps.length <= 1 || steps[steps.length - 1]?._id === activeStepId;
          return (
            <Box
              onClick={() => { if (isLastStep) { onShowSuccess?.(); } else { onNextStep?.(); } }}
              sx={{ px: 2.5, py: 0.75, borderRadius: 1.5, background: col, color: '#fff', fontSize: '1rem', fontWeight: 700, cursor: 'pointer', userSelect: 'none', transition: 'opacity 0.1s', '&:hover': { opacity: 0.88 } }}>
              {isLastStep ? 'Submit' : 'Next'}
            </Box>
          );
        })()}
      </Box>
    </Box>
  );

  if (variant === 'card') {
    return (
      <Box sx={{ width: '100%', maxWidth: 600, background: cardBg, borderRadius: '16px', border: '1px solid #e8edf2', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        {inner}
      </Box>
    );
  }
  return inner;
}


// ─────────────────────────────────────────────────────────────────────────────
// SuccessPreview — inline-editable success screen
// Click any text to edit it directly on the canvas.
// ─────────────────────────────────────────────────────────────────────────────

// Inline editable text field — renders as styled text, becomes a textarea on click
function EditableText({ value, onChange, placeholder, sx: sxProp, multiline }) {
  const [editing, setEditing] = React.useState(false);
  const [draft,   setDraft]   = React.useState(value || '');
  const inputRef = React.useRef(null);

  React.useEffect(() => { setDraft(value || ''); }, [value]);
  React.useEffect(() => { if (editing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); } }, [editing]);

  const commit = () => { setEditing(false); if (draft !== value) onChange(draft); };

  if (editing) {
    const sharedStyle = {
      border: '1.5px solid',
      borderColor: 'primary.main',
      borderRadius: '6px',
      background: 'rgba(255,255,255,0.9)',
      outline: 'none',
      fontFamily: 'inherit',
      textAlign: 'center',
      width: '100%',
      boxSizing: 'border-box',
      padding: '4px 8px',
      resize: 'none',
      display: 'block',
    };
    if (multiline) {
      return (
        <Box component="textarea"
          ref={inputRef}
          value={draft}
          rows={3}
          onChange={e => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={e => { if (e.key === 'Escape') { setEditing(false); setDraft(value || ''); } }}
          onClick={e => e.stopPropagation()}
          sx={{ ...sharedStyle, ...sxProp }}
        />
      );
    }
    return (
      <Box component="input"
        ref={inputRef}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setEditing(false); setDraft(value || ''); } }}
        onClick={e => e.stopPropagation()}
        sx={{ ...sharedStyle, ...sxProp }}
      />
    );
  }

  const displayVal = value || placeholder || '';
  const isEmpty    = !value;
  return (
    <Box
      onClick={e => { e.stopPropagation(); setEditing(true); }}
      title="Click to edit"
      sx={{
        cursor: 'text', display: 'inline-block', width: '100%',
        borderRadius: '6px', border: '1.5px solid transparent',
        transition: 'border-color 0.15s, background 0.15s',
        '&:hover': { borderColor: alpha('#1075BA', 0.35), background: 'rgba(255,255,255,0.6)' },
        ...sxProp,
        color: isEmpty ? alpha(sxProp?.color || '#9ea8b3', 0.5) : sxProp?.color,
      }}
    >
      {displayVal}
    </Box>
  );
}

function SuccessPreview({ successPage, branding, variant, onSuccessChange }) {
  const col     = branding?.primaryColor || '#1565C0';
  const cardBg  = branding?.cardColor    || '#fff';
  const sp      = successPage || {};
  const isFlush = variant === 'flush' || variant === 'incard';
  const editable = !!onSuccessChange;
  const up = (k, v) => onSuccessChange && onSuccessChange({ ...sp, [k]: v });
  const upCta = (cta, k, v) => onSuccessChange && onSuccessChange({ ...sp, [cta]: { ...(sp[cta] || {}), [k]: v } });

  // Shared hover style for editable wrapper
  const editHint = editable ? {
    cursor: 'text',
    borderRadius: '6px',
    border: '1.5px solid transparent',
    transition: 'border-color 0.15s, background 0.15s',
    '&:hover': { borderColor: alpha(col, 0.35), background: 'rgba(255,255,255,0.5)' },
  } : {};

  const inner = (
    <Box sx={{ textAlign: 'center', py: 5, px: 4, flex: isFlush ? 1 : undefined, overflowY: isFlush ? 'auto' : undefined }}>

      {/* Icon */}
      <Box sx={{ width: 72, height: 72, borderRadius: '50%', background: alpha(col, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3, boxShadow: `0 0 0 12px ${alpha(col, 0.06)}` }}>
        <Box sx={{ width: 52, height: 52, borderRadius: '50%', background: col, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CheckCircleOutline sx={{ color: '#fff', fontSize: 28 }} />
        </Box>
      </Box>

      {/* Subtitle */}
      {editable ? (
        <EditableText
          value={sp.subtitle}
          onChange={v => up('subtitle', v)}
          placeholder="TRANSACTION SUCCESSFUL"
          sx={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.2em', textTransform: 'uppercase', color: col, mb: 0.75 }}
        />
      ) : (
        sp.subtitle && <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.2em', textTransform: 'uppercase', color: col, mb: 0.75 }}>{sp.subtitle}</Typography>
      )}

      {/* Title */}
      {editable ? (
        <EditableText
          value={sp.title}
          onChange={v => up('title', v)}
          placeholder="You're registered!"
          sx={{ fontSize: '1.3rem', fontWeight: 800, color: '#1a1a2e', mb: 1.25, lineHeight: 1.2 }}
        />
      ) : (
        <Typography sx={{ fontSize: '1.3rem', fontWeight: 800, color: '#1a1a2e', mb: 1.25, lineHeight: 1.2 }}>
          {sp.title || "You're registered!"}
        </Typography>
      )}

      {/* Message */}
      {editable ? (
        <Box sx={{ maxWidth: 380, mx: 'auto', mb: 3 }}>
          <EditableText
            value={sp.message}
            onChange={v => up('message', v)}
            placeholder="Thank you for signing up. We'll send you a confirmation email shortly."
            multiline
            sx={{ fontSize: '1rem', color: '#7a8494', lineHeight: 1.5 }}
          />
        </Box>
      ) : (
        <Typography sx={{ fontSize: '1rem', color: '#7a8494', mb: 3, maxWidth: 480, mx: 'auto' }}>
          {sp.message || "Thank you for signing up. We'll send you a confirmation email shortly."}
        </Typography>
      )}

      {/* Event details cards */}
      {(sp.showEventDetails ?? true) && (
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', mb: 3, flexWrap: 'wrap' }}>
          {[
            { icon: <CalendarMonthOutlined sx={{ fontSize: 17 }} />, label: 'DATE',     val: 'Nov 24, 2025' },
            { icon: <AccessTimeOutlined   sx={{ fontSize: 17 }} />, label: 'TIME',     val: '10:00 AM EST' },
            { icon: <VideocamOutlined     sx={{ fontSize: 17 }} />, label: 'PLATFORM', val: 'Zoom' },
          ].map(c => (
            <Box key={c.label} sx={{ px: 2, py: 1.5, borderRadius: '10px', border: '1px solid #e8edf2', background: '#fafbfc', minWidth: 100 }}>
              <Box sx={{ color: '#9ea8b3', mb: 0.5 }}>{c.icon}</Box>
              <Typography sx={{ fontSize: '0.56rem', color: '#b0bac8', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{c.label}</Typography>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#3c4555' }}>{c.val}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* CTA buttons */}
      <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', mb: 2.5, flexWrap: 'wrap' }}>
        {(editable || sp.ctaPrimary?.label) && (
          editable ? (
            <Box sx={{ px: 2.5, py: 0.85, borderRadius: 1.5, background: col, color: '#fff', ...editHint }}>
              <EditableText
                value={sp.ctaPrimary?.label}
                onChange={v => upCta('ctaPrimary', 'label', v)}
                placeholder="Primary CTA"
                sx={{ fontSize: '1rem', fontWeight: 700, color: '#fff' }}
              />
            </Box>
          ) : (
            <Box sx={{ px: 2.5, py: 0.85, borderRadius: 1.5, background: col, color: '#fff', fontSize: '1rem', fontWeight: 700 }}>{sp.ctaPrimary.label}</Box>
          )
        )}
        {(editable || sp.ctaSecondary?.label) && (
          editable ? (
            <Box sx={{ px: 2.5, py: 0.85, borderRadius: 1.5, border: `1px solid ${col}`, color: col, ...editHint }}>
              <EditableText
                value={sp.ctaSecondary?.label}
                onChange={v => upCta('ctaSecondary', 'label', v)}
                placeholder="Secondary CTA"
                sx={{ fontSize: '1rem', fontWeight: 600, color: col }}
              />
            </Box>
          ) : (
            <Box sx={{ px: 2.5, py: 0.85, borderRadius: 1.5, border: `1px solid ${col}`, color: col, fontSize: '1rem', fontWeight: 600 }}>{sp.ctaSecondary.label}</Box>
          )
        )}
      </Box>

      {/* Reference number */}
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, background: alpha(col, 0.07), border: `1px solid ${alpha(col, 0.2)}`, borderRadius: 1.5, px: 2, py: 0.75, mb: 2 }}>
        <Typography sx={{ fontSize: '0.875rem', color: col }}>Reference: <strong>PREVIEW-0000</strong></Typography>
      </Box>

      {/* Support text */}
      {editable ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
          <InfoOutlined sx={{ fontSize: 13, color: '#c0c8d4' }} />
          <EditableText
            value={sp.supportText}
            onChange={v => up('supportText', v)}
            placeholder="Questions? Contact support…"
            sx={{ fontSize: '0.875rem', color: '#b0bac8' }}
          />
        </Box>
      ) : (
        sp.supportText && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
            <InfoOutlined sx={{ fontSize: 13, color: '#c0c8d4' }} />
            <Typography sx={{ fontSize: '0.875rem', color: '#b0bac8' }}>{sp.supportText}</Typography>
          </Box>
        )
      )}
    </Box>
  );

  if (variant === 'card') {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3, overflowY: 'auto' }}>
        <Box sx={{ width: '100%', maxWidth: 600, background: cardBg, borderRadius: '16px', border: '1px solid #e8edf2', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          {inner}
        </Box>
      </Box>
    );
  }
  // flush / incard — fill panel and center
  return (
    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflowY: 'auto' }}>
      {inner}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FormCanvas — main export
// Added activeStepId prop so the in-card indicator knows which step is active.
// ─────────────────────────────────────────────────────────────────────────────

export default function FormCanvas({
  layout, branding, settings, isSuccessView,
  currentStep, activeStepId,
  fields, selectedId, onSelect, onDelete, onFieldChange,
  steps, dragHandlers, successPage, onSuccessChange,
  stepHandlers, onSettingsChange, onNextStep, onShowSuccess,
}) {
  const { onDragStart, onDragOver, onDrop, onDropNew, dragOverId } = dragHandlers;
  const col     = branding?.primaryColor    || '#1565C0';
  const bgColor = branding?.backgroundColor || '#EEF2F7';

  const getVariant = () => {
    if (layout === 'image-left' || layout === 'image-right') return 'flush';
    if (layout === 'card-left'  || layout === 'card-right')  return 'incard';
    return 'card';
  };

  const heroText = (align) => (
    <HeroText
      settings={settings}
      align={align}
      selectedId={selectedId}
      onSelect={onSelect}
      onSettingsChange={onSettingsChange}
    />
  );

  const mediaPanel = (side) => (
    <MediaPanel
      branding={branding}
      sx={{ flex: layout.startsWith('card') ? '0 0 40%' : '0 0 35%', minHeight: '100%' }}
      selectedId={selectedId}
      onSelect={onSelect}
    >
      {heroText(side === 'left' ? 'left' : 'right')}
    </MediaPanel>
  );

  // ── Content ──────────────────────────────────────────────────────────────
  const content = isSuccessView
    ? <SuccessPreview successPage={successPage} branding={branding} variant={getVariant()} onSuccessChange={onSuccessChange} />
    : (
      <FormCardPreview
        fields={fields} selectedId={selectedId}
        onSelect={onSelect} onDelete={onDelete}
        onFieldChange={onFieldChange}
        onDragStart={onDragStart} onDragOver={onDragOver}
        onDrop={onDrop} onDropNew={onDropNew}
        dragOverId={dragOverId}
        steps={steps} activeStepId={activeStepId}
        stepHandlers={stepHandlers}
        branding={branding} variant={getVariant()}
        onNextStep={onNextStep}
        onShowSuccess={onShowSuccess}
      />
    );

  if (layout === 'image-top') {
    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', background: bgColor }}>
        <MediaPanel branding={branding} sx={{ height: 260, flexShrink: 0 }} selectedId={selectedId} onSelect={onSelect}>
          <Box sx={{ textAlign: 'center' }}>{heroText('center')}</Box>
        </MediaPanel>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, pb: 5 }}>{content}</Box>
      </Box>
    );
  }
  if (layout === 'image-bg') {
    return (
      <MediaPanel branding={branding} sx={{ flex: 1, overflow: 'hidden', position: 'relative' }} selectedId={selectedId} onSelect={onSelect}>
        <Box sx={{ position: 'absolute', inset: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 2, pb: 4, px: 2, gap: 3 }}>
          {heroText('center')}
          {content}
        </Box>
      </MediaPanel>
    );
  }
  if (layout === 'image-left' || layout === 'image-right') {
    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', overflow: 'hidden' }}>
        {layout === 'image-left'
          ? <>{mediaPanel('left')}<Box sx={{ flex: '0 0 65%', display: 'flex', flexDirection: 'column', background: '#fff', overflow: 'hidden' }}>{content}</Box></>
          : <><Box sx={{ flex: '0 0 65%', display: 'flex', flexDirection: 'column', background: '#fff', overflow: 'hidden' }}>{content}</Box>{mediaPanel('right')}</>
        }
      </Box>
    );
  }
  if (layout === 'card-left' || layout === 'card-right') {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3, background: bgColor, overflowY: 'auto' }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%', maxWidth: 1000, height: '65vh', maxHeight: 720, borderRadius: '16px', overflow: 'hidden', boxShadow: '0 8px 48px rgba(0,0,0,0.14)', border: '1px solid #e8edf2' }}>
          {layout === 'card-left'
            ? <>{mediaPanel('left')}<Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#fff', overflow: 'hidden' }}>{content}</Box></>
            : <><Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#fff', overflow: 'hidden' }}>{content}</Box>{mediaPanel('right')}</>
          }
        </Box>
      </Box>
    );
  }
  return null;
}